import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Button } from "react-bootstrap";
import Select from "react-select";
import {
  getAllStates,
  getRegionsByStateId,
  getBranchesByRegionId,
} from "../../../Utils/Services/spokeMaster";

const BranchFilter = forwardRef(({ onFilter, onClear, loading }, ref) => {
  const [filterData, setFilterData] = useState({
    stateId: null,
    regionId: null,
    branchName: "",
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const [dropdownOptions, setDropdownOptions] = useState({
    states: [],
    regions: [],
  });

  const [selectedOptions, setSelectedOptions] = useState({
    state: null,
    region: null,
  });

  useEffect(() => {
    loadStates();
  }, []);

  const loadStates = async () => {
    try {
      const response = await getAllStates();
      console.log("States API Response:", response); // Debug log
      const actualResponse = response.data;
      
      if (actualResponse && actualResponse.status === "success" && actualResponse.result) {
        const stateOptions = actualResponse.result.map((state) => ({
          value: state.id,
          label: state.stateName,
        }));
        console.log("State Options:", stateOptions); // Debug log
        setDropdownOptions((prev) => ({
          ...prev,
          states: stateOptions,
        }));
      } else {
        console.error("States API Error - Unexpected response structure:", actualResponse);
      }
    } catch (error) {
      console.error("Error loading states:", error);
    }
  };

  const loadRegions = async (stateId) => {
    try {
      const response = await getRegionsByStateId(stateId);
      console.log("Regions API Response:", response); // Debug log
      const actualResponse = response.data;
      
      if (actualResponse && actualResponse.status === "success" && actualResponse.result) {
        const regionOptions = actualResponse.result.map((region) => ({
          value: region.id,
          label: region.regionName,
        }));
        console.log("Region Options:", regionOptions); // Debug log
        setDropdownOptions((prev) => ({
          ...prev,
          regions: regionOptions,
        }));
      } else if (actualResponse && actualResponse.data && !actualResponse.error) {
        // Fallback for different response structure
        const regionOptions = actualResponse.data.map((region) => ({
          value: region.id,
          label: region.regionName,
        }));
        console.log("Region Options (fallback):", regionOptions); // Debug log
        setDropdownOptions((prev) => ({
          ...prev,
          regions: regionOptions,
        }));
      } else if (Array.isArray(actualResponse)) {
        // Handle direct array response
        const regionOptions = actualResponse.map((region) => ({
          value: region.id,
          label: region.regionName,
        }));
        console.log("Region Options (array response):", regionOptions); // Debug log
        setDropdownOptions((prev) => ({
          ...prev,
          regions: regionOptions,
        }));
      } else {
        console.error("Regions API Error - Unexpected response structure:", actualResponse);
      }
    } catch (error) {
      console.error("Error loading regions:", error);
    }
  };


  const handleStateChange = async (selectedOption) => {
    setSelectedOptions((prev) => ({
      ...prev,
      state: selectedOption,
      region: null,
    }));

    setFilterData((prev) => ({
      ...prev,
      stateId: selectedOption?.value || null,
      regionId: null,
      branchName: filterData.branchName, // Keep existing branch name
    }));

    setDropdownOptions((prev) => ({
      ...prev,
      regions: [],
    }));

    if (selectedOption?.value) {
      await loadRegions(selectedOption.value);
    }
  };

  const handleRegionChange = (selectedOption) => {
    setSelectedOptions((prev) => ({
      ...prev,
      region: selectedOption,
    }));

    setFilterData((prev) => ({
      ...prev,
      regionId: selectedOption?.value || null,
      branchName: filterData.branchName, // Keep existing branch name
    }));
  };

  // Handle branch name input change
  const handleBranchNameChange = (e) => {
    const value = e.target.value;
    setFilterData((prev) => ({
      ...prev,
      branchName: value,
    }));
  };

  const handleFilter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isProcessing || loading) {
      console.log("BranchFilter: Already processing, skipping");
      return;
    }
    
    console.log("BranchFilter handleFilter called with:", filterData);
    console.log("BranchFilter handleFilter stack trace:", new Error().stack);
    
    setIsProcessing(true);
    
    // State is no longer mandatory - allow filtering with any combination of fields
    onFilter(filterData);
    
    // Reset processing state after a short delay
    setTimeout(() => {
      setIsProcessing(false);
    }, 1000);
  };

  const handleClear = () => {
    setFilterData({
      stateId: null,
      regionId: null,
      branchName: "",
    });

    setSelectedOptions({
      state: null,
      region: null,
    });

    setDropdownOptions({
      states: dropdownOptions.states,
      regions: [],
    });

    onClear();
  };

  // Expose clear method to parent component
  useImperativeHandle(ref, () => ({
    clear: handleClear
  }));

  return (
    <div className="filter-container p-2 mb-3" style={{ backgroundColor: "transparent", borderRadius: "6px" }}>
      {/* Responsive Flex Layout */}
      <div className="branch-filter-layout">
        {/* First Row - State, Region, Branch (above 1650px: all in one row, below 1650px: fields in one row) */}
        <div className="filter-row filter-row-first">
          {/* State Field */}
          <div className="filter-field">
            <label className="form-label mb-1" style={{ fontSize: '14px', fontWeight: '500' }}>
              State
            </label>
            <Select
              value={selectedOptions.state}
              onChange={handleStateChange}
              options={dropdownOptions.states}
              placeholder="Select State"
              isClearable
              isSearchable
              className="react-select-container"
              classNamePrefix="react-select"
              styles={{
                control: (provided) => ({
                  ...provided,
                  minHeight: '36px',
                  fontSize: '14px'
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  padding: '4px 8px'
                }),
                input: (provided) => ({
                  ...provided,
                  margin: '0px'
                }),
                indicatorsContainer: (provided) => ({
                  ...provided,
                  height: '36px'
                })
              }}
            />
          </div>

          {/* Region Field */}
          <div className="filter-field">
            <label className="form-label mb-1" style={{ fontSize: '14px', fontWeight: '500' }}>
              Region
            </label>
            <Select
              value={selectedOptions.region}
              onChange={handleRegionChange}
              options={dropdownOptions.regions}
              placeholder="Select Region"
              isClearable
              isSearchable
              isDisabled={!selectedOptions.state}
              className="react-select-container"
              classNamePrefix="react-select"
              styles={{
                control: (provided) => ({
                  ...provided,
                  minHeight: '36px',
                  fontSize: '14px'
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  padding: '4px 8px'
                }),
                input: (provided) => ({
                  ...provided,
                  margin: '0px'
                }),
                indicatorsContainer: (provided) => ({
                  ...provided,
                  height: '36px'
                })
              }}
            />
          </div>

          {/* Branch Field */}
          <div className="filter-field">
            <label className="form-label mb-1" style={{ fontSize: '14px', fontWeight: '500' }}>
              Branch
            </label>
            <input
              type="text"
              value={filterData.branchName}
              onChange={handleBranchNameChange}
              placeholder="Enter Branch Name"
              className="form-control"
              style={{
                minHeight: '36px',
                fontSize: '14px',
                border: filterData.branchName ? '2px solid #28a745' : '1px solid #ced4da',
                boxShadow: filterData.branchName ? '0 0 0 0.2rem rgba(40, 167, 69, 0.25)' : 'none'
              }}
            />
          </div>
        </div>

        {/* Second Row - Action Buttons (below 1650px only) */}
        <div className="filter-row filter-row-second">
          <div className="action-buttons">
            <Button
              variant="primary"
              onClick={handleFilter}
              disabled={loading || isProcessing}
              size="sm"
              className="px-3 py-2"
              style={{ fontSize: '14px', minWidth: '80px' }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Filter
                </>
              ) : (
                'Filter'
              )}
            </Button>
            <Button
              variant="outline-secondary"
              onClick={handleClear}
              disabled={loading}
              size="sm"
              className="px-3 py-2"
              style={{ fontSize: '14px', minWidth: '80px' }}
            >
              Clear
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default BranchFilter;
